import React, { useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { Loader2, Check, Eraser, FileSignature, ShieldCheck, Clock, Download } from "lucide-react";
import Barcode from "react-barcode";
import logo from "../assets/logo.jpg";
import { fmtD } from "../lib/date";
import { SmsConsent } from "../components/legal/SmsConsent";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

function SignaturePad({ onChange }) {
  const ref = useRef();
  const drawing = useRef(false);
  const dirty = useRef(false);
  const pos = (e) => { const c = ref.current; const r = c.getBoundingClientRect(); const t = e.touches ? e.touches[0] : e; return { x: (t.clientX - r.left) * (c.width / r.width), y: (t.clientY - r.top) * (c.height / r.height) }; };
  const start = (e) => { drawing.current = true; const ctx = ref.current.getContext("2d"); const p = pos(e); ctx.beginPath(); ctx.moveTo(p.x, p.y); e.preventDefault(); };
  const move = (e) => { if (!drawing.current) return; const ctx = ref.current.getContext("2d"); const p = pos(e); ctx.lineTo(p.x, p.y); ctx.strokeStyle = "#0f172a"; ctx.lineWidth = 2.5; ctx.lineCap = "round"; ctx.stroke(); dirty.current = true; e.preventDefault(); };
  const end = () => { if (drawing.current && dirty.current) onChange(ref.current.toDataURL("image/png")); drawing.current = false; };
  const clear = () => { const c = ref.current; c.getContext("2d").clearRect(0, 0, c.width, c.height); dirty.current = false; onChange(null); };
  return (
    <div>
      <canvas ref={ref} width={520} height={170} data-testid="owner-signature-pad"
        className="w-full h-44 rounded-lg border-2 border-dashed border-slate-300 bg-slate-50 touch-none cursor-crosshair"
        onMouseDown={start} onMouseMove={move} onMouseUp={end} onMouseLeave={end}
        onTouchStart={start} onTouchMove={move} onTouchEnd={end} />
      <button onClick={clear} data-testid="owner-signature-clear" className="mt-1 text-[12px] text-slate-400 hover:text-red-500 flex items-center gap-1"><Eraser className="h-3 w-3" />清除重签 Clear</button>
    </div>
  );
}

export default function ContractSign() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");
  const [sig, setSig] = useState(null);
  const [agreed, setAgreed] = useState(false);
  const [smsOk, setSmsOk] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    fetch(`${BACKEND}/api/public/contract/${token}`)
      .then((r) => { if (!r.ok) throw new Error("invalid"); return r.json(); })
      .then((d) => { setData(d); setDone(d.signed); })
      .catch(() => setErr("链接无效或已失效 Link invalid or expired"));
  }, [token]);

  const submit = async () => {
    if (!sig) return;
    setSubmitting(true);
    try {
      const res = await fetch(`${BACKEND}/api/public/contract/${token}/sign`, {
        method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ signature: sig, smsConsent: smsOk }),
      });
      if (!res.ok) throw new Error();
      setDone(true);
    } catch { setErr("提交失败，请重试 Submit failed"); }
    setSubmitting(false);
  };

  if (err) return <div className="min-h-screen flex items-center justify-center text-slate-500 p-6 text-center">{err}</div>;
  if (!data) return <div className="min-h-screen flex items-center justify-center text-slate-400"><Loader2 className="h-6 w-6 animate-spin mr-2" />加载合同中…</div>;

  const f = data.fields || {};
  const deadline = data.signDeadline ? new Date(data.signDeadline) : null;
  const daysLeft = deadline ? Math.ceil((deadline.getTime() - Date.now()) / 86400000) : null;
  const showIncentive = !done && data.signWithinDays && (data.signDiscountText || daysLeft != null);
  return (
    <div className="min-h-screen bg-slate-100" data-testid="contract-sign-page">
      <div className="bg-slate-900 text-white px-5 py-4 flex items-center gap-2">
        <FileSignature className="h-5 w-5 text-red-500" />
        <div><p className="font-bold">阿里建筑 · 建筑施工合同签署</p><p className="text-[11px] text-slate-300">ALIGCUS Construction · Contract Signing</p></div>
      </div>
      <div className="max-w-[820px] mx-auto p-4">
        {showIncentive && (
          <div data-testid="contract-sign-incentive-banner" className={`rounded-xl border p-4 my-4 flex items-center gap-3 ${daysLeft != null && daysLeft < 0 ? "border-slate-300 bg-slate-50" : "border-amber-300 bg-gradient-to-r from-amber-50 to-orange-50"}`}>
            <Clock className={`h-7 w-7 shrink-0 ${daysLeft != null && daysLeft < 0 ? "text-slate-400" : "text-amber-500"}`} />
            <div className="min-w-0">
              {data.signDiscountText && <p className="text-sm font-bold text-amber-800" data-testid="contract-sign-discount">{data.signDiscountText}</p>}
              {daysLeft != null && (
                daysLeft >= 0
                  ? <p className="text-[13px] text-amber-700" data-testid="contract-sign-daysleft">尽快签署 · 距优惠截止还剩 <span className="font-bold text-red-600 text-base">{daysLeft}</span> 天（{deadline.toISOString().slice(0, 10)} 前）Sign within {daysLeft} day(s)</p>
                  : <p className="text-[13px] text-slate-500" data-testid="contract-sign-daysleft">优惠期已过，仍可随时签署 Offer window passed</p>
              )}
            </div>
          </div>
        )}
        {done ? (
          <div className="bg-white rounded-2xl border border-green-200 p-8 text-center my-6" data-testid="contract-sign-done">
            <ShieldCheck className="h-14 w-14 text-green-500 mx-auto mb-3" />
            <p className="text-lg font-bold text-slate-800">签署完成，感谢！</p>
            <p className="text-sm text-slate-500 mt-1">您的签名已回填至合同。项目顾问会尽快与您联系。</p>
            <p className="text-[11px] text-slate-400 mt-1">Your signature has been recorded. Thank you.</p>
            <a href={`${BACKEND}/api/public/contract/${token}/pdf`} target="_blank" rel="noreferrer" data-testid="contract-sign-download-pdf"
              className="mt-5 inline-flex flex-col items-center justify-center gap-0.5 min-h-[48px] w-full sm:w-auto px-6 py-2.5 rounded-xl bg-slate-900 hover:bg-slate-800 text-white text-sm font-semibold leading-tight">
              <span className="inline-flex items-center gap-2"><Download className="h-4 w-4" />下载签署版合同 PDF</span>
              <span className="text-[11px] font-normal opacity-80">Download signed contract{f.contractNo ? ` · NO. ${f.contractNo}` : ""}</span>
            </a>
            <p className="text-[11px] text-slate-400 mt-2">请妥善留存，本文件含双方签字区与完整付款计划。Keep this copy for your records.</p>
          </div>
        ) : (
          <div className="bg-white rounded-2xl border border-slate-200 p-5 my-4" data-testid="contract-sign-form">
            <p className="text-sm text-slate-600 mb-3">尊敬的 <span className="font-semibold text-slate-800">{data.ownerName || "业主"}</span>，请阅读以下合同要点并在下方签名。</p>
            <div className="rounded-lg border border-slate-100 bg-slate-50 p-4 max-h-[46vh] overflow-y-auto text-[12.5px] leading-relaxed text-slate-700 space-y-1.5" data-testid="contract-sign-summary">
              {(f.projectName || f.consultant || f.contractDate || f.contractNo) && (
                <div className="mb-3 rounded-lg bg-white border border-slate-200 overflow-hidden" data-testid="contract-sign-header">
                  <div className="flex items-center justify-between gap-3 px-3 py-2.5 bg-slate-50 border-b border-slate-200">
                    <img src={logo} alt="ALIGCUS" data-testid="contract-sign-logo" className="h-9 w-auto object-contain" />
                    {f.contractNo && (
                      <div className="text-right" data-testid="contract-sign-barcode">
                        <Barcode value={String(f.contractNo)} format="CODE128" width={1.3} height={34} fontSize={10} margin={0} displayValue={true} />
                      </div>
                    )}
                  </div>
                  <div className="divide-y divide-slate-100">
                    {f.projectName && <div className="flex items-start gap-2 px-3 py-2"><span className="text-slate-500 shrink-0 w-28">项目名称 Project</span><span className="text-red-600 font-semibold flex-1">{f.projectName}</span></div>}
                    {f.consultant && <div className="flex items-start gap-2 px-3 py-2"><span className="text-slate-500 shrink-0 w-28">建筑顾问 Consultant</span><span className="text-slate-700 flex-1">{f.consultant}</span></div>}
                    {f.contractDate && <div className="flex items-start gap-2 px-3 py-2"><span className="text-slate-500 shrink-0 w-28">日期 Date</span><span className="text-slate-700 flex-1">{fmtD(f.contractDate)}</span></div>}
                    {f.contractNo && <div className="flex items-start gap-2 px-3 py-2"><span className="text-slate-500 shrink-0 w-28">合同编号 No.</span><span className="text-slate-700 flex-1">{f.contractNo}</span></div>}
                  </div>
                </div>
              )}
              <p><span className="font-semibold">业主 Owner：</span><span className="text-red-600 font-semibold">{f.ownerName}</span>{[f.ownerPhone, f.ownerEmail].filter(Boolean).map((v) => ` · ${v}`).join("")}</p>
              <p><span className="font-semibold">项目地址 Address：</span><span className="text-red-600 font-semibold">{f.ownerAddress}</span></p>
              <p><span className="font-semibold">承包商 Contractor：</span>Arcus Construction Inc · {f.contractorAddress}</p>
              <p><span className="font-semibold">合同总价 Total：</span><span className="text-red-600 font-semibold">{f.totalPrice}</span></p>
              <p><span className="font-semibold">建筑面积 Area：</span>扩建 {f.additionSf} sf · ADU {f.aduSf} sf · 合计 {(data.auto || {}).totalSf} sf</p>
              <div className="pt-2 border-t border-slate-200 text-[12px] text-slate-500">
                本合同包含完整的付款计划、施工延误、保险、争议解决(仲裁)、业主须知(机械留置权警告)及三天取消权等条款。签署即表示您已阅读并同意合同全部内容。
              </div>
              <a href={`${BACKEND}/api/public/contract/${token}/pdf`} target="_blank" rel="noreferrer" data-testid="contract-sign-viewpdf" className="inline-flex items-center gap-1 text-[12px] text-indigo-600 underline mt-1"><FileSignature className="h-3.5 w-3.5" />查看完整合同 PDF View full contract</a>
              {data.hasConcept && (
                <a href={`${BACKEND}/api/public/contract/${token}/concept`} target="_blank" rel="noreferrer" data-testid="contract-sign-concept" className="inline-flex items-center gap-1 text-[12px] text-emerald-700 underline mt-1 ml-3"><FileSignature className="h-3.5 w-3.5" />查看已确认概念方案 View approved concept design</a>
              )}
            </div>
            <label className="flex items-start gap-2 mt-4 text-[13px] text-slate-700 cursor-pointer" data-testid="contract-sign-agree">
              <input type="checkbox" checked={agreed} onChange={(e) => setAgreed(e.target.checked)} className="mt-0.5" />
              <span>我已阅读并理解合同全部条款（含仲裁条款与三天取消权），同意受其约束。</span>
            </label>
            <div className="mt-2 rounded-lg border border-slate-100 bg-slate-50 px-3 py-2"><SmsConsent value={smsOk} onChange={setSmsOk} testid="contract-sign-sms-consent" /></div>
            <div className="mt-4">
              <p className="text-[13px] font-semibold text-slate-700 mb-1">业主签名 Owner Signature</p>
              <SignaturePad onChange={setSig} />
            </div>
            <button onClick={submit} disabled={!agreed || !sig || submitting} data-testid="contract-sign-submit"
              className="mt-4 w-full h-11 rounded-xl bg-red-600 hover:bg-red-700 disabled:opacity-40 text-white font-semibold flex items-center justify-center gap-2">
              {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4" />}确认签署 Confirm Signature
            </button>
            <p className="text-[11px] text-slate-400 mt-2 text-center">签署后签名将自动回填至合同 PDF。</p>
          </div>
        )}
      </div>
    </div>
  );
}
